import React, { useEffect, useState } from 'react';
import { FileText, Loader, ExternalLink, Download } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDateTime } from '../../utils/formatDate';

/**
 * Phase 18 — "Form Activity" tab, drop-in for any CRM record detail page.
 * Shows which form submissions created/touched this record, using the
 * crmRecords link FormCrmMappingService writes on every submission
 * (Phase 7/15/16/17). Read-only — this is a history view, not another
 * place to edit the record.
 *
 * @param {string} recordType one of 'lead' | 'contact' | 'account' | 'deal'
 * @param {string} recordId   the record's own _id
 */
export default function FormActivityPanel({ recordType, recordId }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const downloadPdf = async (submission) => {
    setDownloadingId(submission._id);
    try {
      const res = await api.get(`/forms/${submission.formId._id}/submissions/${submission._id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `submission_${submission._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to download PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/forms/by-record/${recordType}/${recordId}`);
        if (!cancelled) setSubmissions(res.data.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recordType, recordId]);

  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader size={20} className="animate-spin text-[#706E6B]" /></div>;
  }

  if (!submissions.length) {
    return (
      <div className="text-center py-10">
        <FileText size={28} className="mx-auto mb-2 text-[#D8DDE6]" />
        <p className="text-[13px] text-[#706E6B]">No form submissions linked to this record yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {submissions.map((s) => {
        const expanded = expandedId === s._id;
        const values = s.values || {};
        return (
          <div key={s._id} className="border border-[#D8DDE6] rounded-[4px] overflow-hidden">
            <button
              type="button"
              onClick={() => setExpandedId(expanded ? null : s._id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F3F3F3]"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={15} className="text-[#0176D3] shrink-0" />
                <span className="text-[13px] font-bold text-[#181818] truncate">{s.formId?.name || 'Untitled Form'}</span>
              </div>
              <span className="text-[11px] text-[#706E6B] shrink-0 ml-3">{formatDateTime(s.createdAt)}</span>
            </button>
            {expanded && (
              <div className="px-4 py-3 border-t border-[#F0F1F3] bg-[#FAFAFA] space-y-1.5">
                {Object.entries(values).length === 0 && (
                  <p className="text-[12px] text-[#706E6B]">No field values recorded.</p>
                )}
                {Object.entries(values).map(([key, val]) => (
                  <div key={key} className="flex items-start gap-2 text-[12px]">
                    <span className="text-[#706E6B] font-mono shrink-0">{key}:</span>
                    <span className="text-[#181818] break-words">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                ))}
                <div className="flex items-center gap-4 pt-1">
                  {s.formId?._id && (
                    <a
                      href={`/forms/${s.formId._id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0176D3] hover:underline"
                    >
                      Open form <ExternalLink size={11} />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => downloadPdf(s)}
                    disabled={downloadingId === s._id}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-[#706E6B] hover:text-[#181818] disabled:opacity-50"
                  >
                    {downloadingId === s._id ? <Loader size={11} className="animate-spin" /> : <Download size={11} />}
                    Download PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
